import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import {
  Tag, TagLink, TagPool, findTagByName,
  getScopedTags, getEntityTagLinks, createTag, attachTag, detachTag,
} from '../services/tagService';

// Tag editor shared by the Task Detail Drawer and the Decision detail panel.
// One interaction pattern, two data pools — the pool prop decides which.
//
// Suggestion rules (locked UX spec):
//  • Autocomplete pool = Tenant tags + this project's Project tags, rendered
//    identically regardless of scope (no badge).
//  • Matching is case-insensitive, enforced here in application code.
//  • "Create new tag: '<value>'" appears only when the typed value has no
//    case-insensitive exact match. It must be explicitly chosen (click or
//    arrow+Enter). Plain Enter on an unmatched value does nothing — the
//    create option is never auto-highlighted.

interface Props {
  pca: IPublicClientApplication;
  pool: TagPool;
  projectId: string;
  /**
   * Live mode: the existing task/decision the junction records belong to.
   * Omit BOTH for staging mode (creation modals — the entity has no ID yet):
   * selected tags are held in the controlled stagedTags/onStagedChange pair
   * and no junction records are written. New-tag creation still writes the
   * tag pool record immediately; only the junction is deferred — the caller
   * commits staged tags with attachTags() after the entity is created, or
   * simply discards them if creation fails (nothing to roll back).
   */
  entityId?: string;
  entityName?: string;
  stagedTags?: Tag[];
  onStagedChange?: (tags: Tag[]) => void;
  onChanged?: () => void;
}

const CHIP: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  background: 'var(--surface-2)', color: 'var(--ink-2)',
  border: '0.5px solid var(--border-light)',
  borderRadius: 'var(--r-sm)', padding: '2px 4px 2px 8px',
  fontSize: 11, fontWeight: 500, fontFamily: 'var(--font-ui)',
};

export default function TagSection({ pca, pool, entityId, entityName, projectId, stagedTags, onStagedChange, onChanged }: Props) {
  const staging = !entityId;
  const staged = stagedTags ?? [];
  const [links, setLinks] = useState<TagLink[] | null>(null);
  const [scoped, setScoped] = useState<Tag[]>([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLinks(null);
    Promise.all([
      // Staging mode has no entity yet — chips come from stagedTags, so
      // there are no junction rows to load.
      entityId ? getEntityTagLinks(pca, pool, entityId) : Promise.resolve([]),
      getScopedTags(pca, pool, projectId),
    ])
      .then(([l, s]) => { if (!cancelled) { setLinks(l); setScoped(s); } })
      .catch(e => { if (!cancelled) { setLinks([]); setError(e instanceof Error ? e.message : 'Failed to load tags'); } });
    return () => { cancelled = true; };
  }, [pca, pool, entityId, projectId]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const appliedIds = useMemo(
    () => new Set(staging ? (stagedTags ?? []).map(t => t.id) : (links ?? []).map(l => l.tag.id)),
    [staging, stagedTags, links]
  );

  // Case-insensitive substring suggestions, minus already-applied tags.
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return scoped.filter(t =>
      !appliedIds.has(t.id) && (!q || t.name.toLowerCase().includes(q))
    );
  }, [scoped, appliedIds, query]);

  // Create option only when there's no case-insensitive exact match anywhere
  // in the scoped pool (applied or not) — dedupe happens here, in app code.
  const exactMatch = useMemo(() => findTagByName(scoped, query), [scoped, query]);
  const showCreate = query.trim().length > 0 && !exactMatch;
  const optionCount = suggestions.length + (showCreate ? 1 : 0);
  const createIndex = showCreate ? suggestions.length : -1;

  // Default highlight sits on the first existing suggestion. The create
  // option is deliberately never the default, so plain Enter can't create.
  useEffect(() => {
    setHighlight(suggestions.length > 0 ? 0 : -1);
  }, [query, suggestions.length, showCreate]);

  const select = async (tag: Tag) => {
    if (busy) return;
    if (staging) {
      // Deferred: no junction write until the parent entity exists.
      if (!appliedIds.has(tag.id)) onStagedChange?.([...staged, tag]);
      setQuery('');
      setOpen(false);
      return;
    }
    setBusy(true); setError(null);
    try {
      const link = await attachTag(pca, pool, entityId!, entityName ?? '', tag);
      setLinks(prev => [...(prev ?? []), link]);
      setScoped(prev => prev.map(t => t.id === link.tag.id ? link.tag : t));
      setQuery('');
      setOpen(false);
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add tag');
    } finally { setBusy(false); }
  };

  const createAndSelect = async () => {
    const name = query.trim();
    if (!name || busy) return;
    // Re-check dedupe at commit time: someone may have created this tag since
    // the scoped list loaded — and never trust casing.
    const existing = findTagByName(scoped, name);
    if (existing) { await select(existing); return; }
    setBusy(true); setError(null);
    try {
      const fresh = await createTag(pca, pool, projectId, name);
      setScoped(prev => [...prev, fresh].sort((a, b) => a.name.localeCompare(b.name)));
      setBusy(false);
      await select(fresh);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create tag');
      setBusy(false);
    }
  };

  const remove = async (link: TagLink) => {
    if (busy) return;
    setBusy(true); setError(null);
    const prev = links;
    setLinks(l => (l ?? []).filter(x => x.linkId !== link.linkId));
    try {
      await detachTag(pca, pool, link.linkId);
      onChanged?.();
    } catch (e) {
      setLinks(prev); // snap back
      setError(e instanceof Error ? e.message : 'Failed to remove tag');
    } finally { setBusy(false); }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setHighlight(h => (optionCount === 0 ? -1 : (h + 1) % optionCount));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => (optionCount === 0 ? -1 : (h <= 0 ? optionCount - 1 : h - 1)));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (!open || highlight < 0) return; // plain Enter on unmatched value: no-op
      if (highlight === createIndex) createAndSelect();
      else if (suggestions[highlight]) select(suggestions[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  // Staged chips remove locally; live chips delete their junction record.
  const chips: { key: string; name: string; onRemove: () => void }[] = staging
    ? staged.map(t => ({
        key: t.id, name: t.name,
        onRemove: () => onStagedChange?.(staged.filter(x => x.id !== t.id)),
      }))
    : (links ?? []).map(l => ({ key: l.linkId, name: l.tag.name, onRemove: () => remove(l) }));

  return (
    <div ref={wrap} style={{ position: 'relative' }}>
      {/* Applied tags as removable chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: chips.length ? 8 : 0 }}>
        {!staging && links === null ? (
          <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>Loading tags…</span>
        ) : (
          chips.map(chip => (
            <span key={chip.key} style={CHIP}>
              {chip.name}
              <button
                type="button"
                aria-label={`Remove tag ${chip.name}`}
                onClick={chip.onRemove}
                disabled={busy}
                style={{
                  background: 'transparent', border: 'none', padding: '0 3px',
                  color: 'var(--ink-4)', cursor: 'pointer', fontSize: 12, lineHeight: 1,
                }}
                onMouseEnter={e => (e.currentTarget.style.color = 'var(--ink)')}
                onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-4)')}
              >×</button>
            </span>
          ))
        )}
      </div>

      {/* Free-type input with autocomplete */}
      <input
        value={query}
        placeholder="Add a tag…"
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        style={{ width: '100%', fontSize: 13 }}
      />

      {open && optionCount > 0 && (
        <div style={{
          position: 'absolute', left: 0, right: 0, top: '100%', marginTop: 4,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)', boxShadow: 'var(--shadow-panel)',
          zIndex: 60, maxHeight: 220, overflowY: 'auto', padding: 4,
        }}>
          {suggestions.map((t, i) => (
            <div
              key={t.id}
              onMouseDown={e => { e.preventDefault(); select(t); }}
              onMouseEnter={() => setHighlight(i)}
              style={{
                padding: '6px 10px', fontSize: 13, color: 'var(--ink)',
                borderRadius: 'var(--r-sm)', cursor: 'pointer',
                background: highlight === i ? 'var(--brand-mist)' : 'transparent',
              }}
            >
              {t.name}
            </div>
          ))}
          {showCreate && (
            <div
              onMouseDown={e => { e.preventDefault(); createAndSelect(); }}
              onMouseEnter={() => setHighlight(createIndex)}
              style={{
                padding: '6px 10px', fontSize: 13,
                color: 'var(--brand)', fontWeight: 500,
                borderRadius: 'var(--r-sm)', cursor: 'pointer',
                background: highlight === createIndex ? 'var(--brand-mist)' : 'transparent',
                borderTop: suggestions.length ? '0.5px solid var(--border-light)' : 'none',
              }}
            >
              Create new tag: '{query.trim()}'
            </div>
          )}
        </div>
      )}

      {error && <div style={{ marginTop: 6, fontSize: 11, color: 'var(--danger)' }}>{error}</div>}
    </div>
  );
}
